import React, { useRef, useEffect, useState } from "react";
import { Menu, Transition } from "@headlessui/react";
import { FaChevronRight } from "react-icons/fa";

const MenuItem = ({ item, onClose, renderItem }) => {
  if (item.submenu) {
    return (
      <Menu as="div" className="relative inline-block text-left w-full">
        <Menu.Button className="flex justify-between items-center w-full px-2 py-1 text-xs text-gray-300 hover:bg-gray-600 hover:text-white">
          <span className="flex items-center">
            {item.icon && (
              <item.icon className="mr-2 h-3 w-3" aria-hidden="true" />
            )}
            {item.label}
          </span>
          <FaChevronRight className="ml-2 h-3 w-3" />
        </Menu.Button>
        <Transition
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute left-full top-0 w-48 mt-0 origin-top-right bg-gray-700 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            {item.submenu.map((subItem, index) => (
              <MenuItem
                key={index}
                item={subItem}
                onClose={onClose}
                renderItem={renderItem}
              />
            ))}
          </Menu.Items>
        </Transition>
      </Menu>
    );
  }

  const renderLabel = (label) => {
    const match = label.match(/(.*)\[(.*)\]$/);
    if (match) {
      return (
        <div className="flex justify-between items-center w-full">
          <span>{match[1].trim()}</span>
          <span className="text-gray-400 ml-4">{match[2]}</span>
        </div>
      );
    }
    return label;
  };

  return (
    <Menu.Item>
      {({ active }) => (
        <button
          className={`${
            active ? "bg-gray-600 text-white" : "text-gray-300"
          } group flex rounded-md items-center w-full px-2 py-1 text-xs ${
            item.disabled
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-gray-600 rounded-none"
          }`}
          onClick={(e) => {
            if (item.onClick) {
              item.onClick(e);
            } else if (!item.preventAutoClose) {
              onClose();
            }
          }}
          disabled={item.disabled}
          title={item.tooltip}
        >
          {renderItem ? (
            renderItem(item)
          ) : (
            <>
              {item.icon && (
                <item.icon className="mr-2 h-3 w-3" aria-hidden="true" />
              )}
              {renderLabel(item.label)}
            </>
          )}
        </button>
      )}
    </Menu.Item>
  );
};

const ContextMenu = ({
  items,
  x,
  y,
  onClose,
  renderItem,
  preventAutoClose = false,
  className = "",
  parentRef,
}) => {
  const menuRef = useRef(null);
  const [position, setPosition] = useState({ x, y });

  useEffect(() => {
    const updatePosition = () => {
      if (menuRef.current) {
        const menuRect = menuRef.current.getBoundingClientRect();
        let newX = x;
        let newY = y;

        if (parentRef && parentRef.current) {
          // If parentRef is provided, position relative to parent
          const parentRect = parentRef.current.getBoundingClientRect();

          // Adjust horizontal position
          if (x + menuRect.width > parentRect.width) {
            newX = parentRect.width - menuRect.width;
          }

          // Adjust vertical position
          if (y + menuRect.height > parentRect.height) {
            newY = parentRect.height - menuRect.height;
          }

          // Ensure the menu doesn't go off the left or top edge of parent
          newX = Math.max(0, newX);
          newY = Math.max(0, newY);
        } else {
          // If no parentRef, position relative to viewport
          if (x + menuRect.width > window.innerWidth) {
            newX = window.innerWidth - menuRect.width;
          }

          if (y + menuRect.height > window.innerHeight) {
            newY = window.innerHeight - menuRect.height;
          }

          // Ensure the menu doesn't go off the left or top edge of viewport
          newX = Math.max(0, newX);
          newY = Math.max(0, newY);
        }

        setPosition({ x: newX, y: newY });
      }
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("resize", updatePosition);
    };
  }, [x, y, parentRef]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const handleItemClick = (item, e) => {
    if (item.onClick) {
      item.onClick(e);
    }

    if (!preventAutoClose) {
      onClose();
    }
  };

  if (items.length === 0) {
    return null;
  }

  return (
    <div
      ref={menuRef}
      className={`${parentRef ? "absolute" : "fixed"} z-[9999] ${className}`}
      style={{ top: `${position.y}px`, left: `${position.x}px` }}
    >
      <Transition
        show={true}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu>
          <div className="px-1 py-1 bg-gray-700 rounded-md shadow-lg ring-1 ring-black ring-opacity-5">
            {items.map((item, index) => (
              <MenuItem
                key={index}
                item={item}
                onClose={onClose}
                renderItem={renderItem}
              />
            ))}
          </div>
        </Menu>
      </Transition>
    </div>
  );
};

export default ContextMenu;
